import React, { useState } from "react";
import { toast } from "react-toastify";
import SlidePanel from "../../components/SlidePanel/SlidePanel";
import * as hrmsService from "../../services/hrmsService";
import { DetailGrid, LoadState, SecondaryButton } from "./hrComponents";
import { formatDate, formatDays, formatRange, statusBadge, statusLabel, useLoad } from "./hrUi";

const SESSION_TEXT = { FULL: "Full day", FIRST_HALF: "First half", SECOND_HALF: "Second half" };

// One leave request: its days, and what the caller can still do with it.
export default function LeaveRequestDrawer({ requestId, onClose, onChanged, onResubmit }) {
  const { data, loading, error, reload } = useLoad(() => hrmsService.getLeaveRequest(requestId), [requestId], { enabled: !!requestId });
  const [busy, setBusy] = useState(false);

  const run = async (fn, okText) => {
    setBusy(true);
    try {
      const res = await fn();
      if (res?.success) {
        toast.success(res.message || okText);
        onChanged && onChanged();
        onClose();
      } else toast.error(hrmsService.responseMessage(res));
    } finally {
      setBusy(false);
    }
  };

  const openAttachment = async () => {
    try {
      const res = await hrmsService.getLeaveAttachmentUrl(requestId);
      if (res?.data?.url) window.open(res.data.url, "_blank", "noopener");
    } catch (e) {
      toast.error(e.message || "Could not open the document");
    }
  };

  const footer = (
    <>
      <SecondaryButton onClick={onClose}>Close</SecondaryButton>
      {data && (data.status === "PENDING" || data.status === "SENT_BACK") ? (
        <SecondaryButton disabled={busy} onClick={() => run(() => hrmsService.withdrawLeave(requestId), "Withdrawn")}>
          Withdraw
        </SecondaryButton>
      ) : null}
      {data && data.status === "APPROVED" ? (
        <SecondaryButton disabled={busy} onClick={() => run(() => hrmsService.cancelLeave(requestId), "Cancellation sent")}>
          Cancel leave
        </SecondaryButton>
      ) : null}
      {data && data.status === "SENT_BACK" ? (
        <button
          type="button"
          className="btn Button primaryBackground text-white px-4"
          onClick={() => {
            onResubmit(data);
            onClose();
          }}
        >
          Change and resubmit
        </button>
      ) : null}
    </>
  );

  return (
    <SlidePanel show={!!requestId} onHide={onClose} title="Leave request" footer={footer} width={480}>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {data ? (
          <>
            <div className="d-flex justify-content-between align-items-start mb-3">
              <div>
                <div style={{ fontWeight: 600 }}>{data.leave_type_name}</div>
                <div style={{ fontSize: 13, color: "#6B7280" }}>{data.full_name}</div>
              </div>
              <span style={statusBadge(data.status)}>{statusLabel(data.status)}</span>
            </div>
            <DetailGrid
              items={[
                ["Dates", formatRange(data.from_date, data.to_date)],
                ["Days", formatDays(data.total_days)],
                ["Start", SESSION_TEXT[data.from_session] || data.from_session],
                ["End", SESSION_TEXT[data.to_session] || data.to_session],
                ["Reason", data.reason],
                ["Backdated", data.is_backdated ? "Yes" : "No"],
              ]}
            />
            {data.attachment_key ? (
              <button type="button" className="btn btn-link px-0 mt-2" onClick={openAttachment}>
                View supporting document
              </button>
            ) : null}
            <h6 className="mt-4 mb-2">Days</h6>
            {(data.days || []).map((d) => (
              <div key={d.leave_date} className="d-flex justify-content-between" style={{ fontSize: 13, padding: "5px 0", borderBottom: "1px solid #F3F4F6", opacity: d.status === "CANCELLED" ? 0.5 : 1 }}>
                <span>{formatDate(d.leave_date)}</span>
                <span style={{ color: "#6B7280" }}>
                  {d.counted ? (d.fraction === 0.5 ? "Half day" : "Full day") : "Day off (not counted)"}
                  {d.status !== "ACTIVE" ? ` · ${statusLabel(d.status)}` : ""}
                </span>
              </div>
            ))}
          </>
        ) : null}
      </LoadState>
    </SlidePanel>
  );
}
